import type { GroupFormOption } from '@/api/resourceCenterApi'

// Importing a group's forms from a spreadsheet (deck slide 6: "Drag and Drop
// and Excel … to select the Forms for each Group"). Pure: the component reads
// the file, these decide what it means.

/** Header cells that name the form column; otherwise the first column is used. */
const FORM_HEADERS = ['form', 'form name', 'forms', 'name']

const norm = (s: string) => s.trim().replace(/\s+/g, ' ').toLowerCase()

/**
 * The form names in a sheet's rows. A header row naming the form column is
 * skipped and picks the column; otherwise every row's first cell is a name.
 */
export function namesFromRows(rows: string[][]): string[] {
  const nonEmpty = rows.filter(r => r.some(c => c.trim() !== ''))
  if (nonEmpty.length === 0) return []
  const header = nonEmpty[0].map(norm)
  const col = header.findIndex(h => FORM_HEADERS.includes(h))
  const body = col >= 0 ? nonEmpty.slice(1) : nonEmpty
  const at = col >= 0 ? col : 0
  return body.map(r => (r[at] ?? '').trim()).filter(Boolean)
}

/** CSV text to rows — quoted cells, embedded commas and doubled quotes included. */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = []
  let row: string[] = []
  let cell = ''
  let quoted = false
  for (let i = 0; i < text.length; i++) {
    const ch = text[i]
    if (quoted) {
      if (ch === '"' && text[i + 1] === '"') {
        cell += '"'
        i++
      } else if (ch === '"') quoted = false
      else cell += ch
    } else if (ch === '"' && cell === '') quoted = true
    else if (ch === ',' || ch === ';' || ch === '\t') {
      row.push(cell)
      cell = ''
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i++
      row.push(cell)
      rows.push(row)
      row = []
      cell = ''
    } else cell += ch
  }
  if (cell !== '' || row.length > 0) {
    row.push(cell)
    rows.push(row)
  }
  return rows
}

export interface FormImportResult {
  /** Family ids found, in file order, each once. */
  matched: string[]
  /** Names that match no current form, as written. */
  unmatched: string[]
}

/** Match names to the center's current forms, ignoring case and spacing. */
export function matchFormNames(names: string[], forms: GroupFormOption[]): FormImportResult {
  const byName = new Map<string, string>()
  for (const f of forms) if (!byName.has(norm(f.name))) byName.set(norm(f.name), f.familyId)
  const matched: string[] = []
  const unmatched: string[] = []
  for (const n of names) {
    const id = byName.get(norm(n))
    if (id) {
      if (!matched.includes(id)) matched.push(id)
    } else if (!unmatched.some(u => norm(u) === norm(n))) unmatched.push(n)
  }
  return { matched, unmatched }
}
